import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getFamilyByInviteToken, getFamilyDoc, acceptInvite } from '../../services/firebase';

const StudentAcceptInvite = () => {
  const { token, familyId: familyIdParam } = useParams();
  const { authUser, userDoc } = useAuth();
  const navigate = useNavigate();

  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token || token === 'pending') {
      setLoading(false);
      return;
    }
    if (familyIdParam) {
      getFamilyDoc(familyIdParam)
        .then(f => {
          if (!f || f.inviteToken !== token) {
            setFamily(null);
          } else {
            setFamily(f);
          }
        })
        .catch(() => setError('Could not load invite.'))
        .finally(() => setLoading(false));
    } else {
      getFamilyByInviteToken(token)
        .then(f => setFamily(f))
        .catch(() => setError('Could not load invite.'))
        .finally(() => setLoading(false));
    }
  }, [token, familyIdParam]);

  const handleAccept = async () => {
    if (!authUser) {
      const inviteParam = familyIdParam ? `${familyIdParam}/${token}` : token;
      sessionStorage.setItem('pendingInviteToken', inviteParam);
      navigate(`/signup/student?invite=${inviteParam}`);
      return;
    }
    if (userDoc?.role !== 'student') {
      setError('This invite is for students only.');
      return;
    }
    if (userDoc?.familyId) {
      navigate('/');
      return;
    }
    setAccepting(true);
    try {
      await acceptInvite(family.id, authUser.uid);
      sessionStorage.removeItem('pendingInviteToken');
      sessionStorage.setItem('inviteJustAccepted', '1');
      setAccepted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="screen screen--centered screen--no-nav">
        <div className="spinner" />
      </div>
    );
  }

  if (token === 'pending') {
    return (
      <div className="screen screen--centered screen--no-nav">
        <div style={{ textAlign: 'center', maxWidth: 340 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <h2>Waiting for your invite</h2>
          <p style={{ color: 'var(--color-text-2)', marginTop: 12, lineHeight: 1.6 }}>
            Ask your parent to send you the invite link from their dashboard.
            Once you have it, tap the link on your phone to connect.
          </p>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="screen screen--centered screen--no-nav">
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 72, marginBottom: 16 }}>🎉</div>
          <h1 style={{ fontSize: '1.75rem', marginBottom: 12 }}>You're connected!</h1>
          <p style={{ color: 'var(--color-text-2)', lineHeight: 1.6, marginBottom: 32 }}>
            Your wallet is ready. Start tracking your spending and saving toward your goals.
          </p>
          <a href="/signin" className="btn btn--primary btn--full btn--lg"
            style={{ display: 'block', textAlign: 'center' }}>
            Open my wallet →
          </a>
        </div>
      </div>
    );
  }

  if (authUser && userDoc?.familyId) {
    navigate('/');
    return null;
  }

  if (!family || error) {
    return (
      <div className="screen screen--centered screen--no-nav">
        <div style={{ textAlign: 'center', maxWidth: 340 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
          <h2>Invite not found</h2>
          <p style={{ color: 'var(--color-text-2)', marginTop: 12, lineHeight: 1.6 }}>
            {error || 'This invite link may have already been used or expired. Ask your parent to send a new one.'}
          </p>
          <Link to="/signin" className="btn btn--primary" style={{ marginTop: 32, display: 'inline-flex' }}>
            Go to Sign In
          </Link>
        </div>
      </div>
    );
  }

  const firstName = family?.studentName?.split(' ')[0] || 'there';
  const allowance = family?.allowance;

  return (
    <div className="screen screen--centered screen--no-nav">
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>👋</div>
          <h1 style={{ fontSize: '1.75rem', marginBottom: 8 }}>Hey {firstName}!</h1>
          <p style={{ color: 'var(--color-text-2)', lineHeight: 1.6 }}>
            Your parent set up a wallet for you. Here's what's included:
          </p>
        </div>

        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--color-text-3)',
            letterSpacing: '0.05em', marginBottom: 16 }}>YOUR WALLET SETUP</div>
          <div className="stack stack-4">
            <SummaryRow icon="💰" label="Allowance" value={`$${allowance?.amount} ${allowance?.frequency}`} />
            {family?.match?.enabled && (
              <SummaryRow icon="🎯" label="Savings match"
                value={family.match.type === 'percentage'
                  ? `${family.match.value}% of what you save`
                  : `$${family.match.value} per dollar saved`} />
            )}
            {family?.match?.enabled && family?.match?.monthlyCap > 0 && (
              <SummaryRow icon="📊" label="Monthly cap" value={`$${family.match.monthlyCap}/month`} />
            )}
          </div>
        </div>

        {error && <div className="alert alert--error" style={{ marginBottom: 16 }}>{error}</div>}

        <div className="stack stack-3">
          <button className="btn btn--primary btn--full btn--lg"
            onClick={handleAccept} disabled={accepting}>
            {accepting
              ? <span className="spinner spinner--white" style={{ width: 20, height: 20 }} />
              : authUser ? 'Accept & Connect' : 'Create my account & connect'}
          </button>
          {!authUser && (
            <div style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--color-text-2)' }}>
              Already have an account?{' '}
              <button style={{ background: 'none', border: 'none', color: 'var(--color-primary)',
                fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }}
                onClick={() => {
                  const inviteParam = familyIdParam ? `${familyIdParam}/${token}` : token;
                  sessionStorage.setItem('pendingInviteToken', inviteParam);
                  navigate(`/signin?invite=${inviteParam}`);
                }}>
                Sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryRow = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <span style={{ fontSize: 20 }}>{icon}</span>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-3)' }}>{label}</div>
      <div style={{ fontWeight: 600, marginTop: 2 }}>{value}</div>
    </div>
  </div>
);

export default StudentAcceptInvite;
